import { ORDER } from '../core/systems.js';
import { clamp } from '../core/mathx.js';

const KEYMAP = {
  KeyW:'up', KeyS:'down', KeyA:'left', KeyD:'right',
  ArrowUp:'up', ArrowDown:'down', ArrowLeft:'left', ArrowRight:'right',
  KeyJ:'light', KeyK:'heavy', KeyL:'musou', Space:'dodge', ShiftLeft:'sprint', ShiftRight:'sprint',
  KeyE:'interact', KeyQ:'lockon', KeyR:'special', KeyF:'mount', Escape:'pause', KeyP:'pause', Tab:'map',
  // 'special' is the jump/air-launch action. Space is already the dodge, so jump
  // also lives on X — without a second binding the whole air combo was only
  // reachable via R, which reads as a debug key rather than a control.
  KeyX:'special', KeyG:'guard',
  // DEV_PLAN Step 5. The four tactical orders. Digits were the only unbound
  // row on the board and they read as orders in every game that has them.
  Digit1:'order1', Digit2:'order2', Digit3:'order3', Digit4:'order4',
  // DEV_PLAN Step 6. Answering an officer's challenge. C and not E or F: E is
  // already a guard alias (player.js reads `interact` as guard held) and F is
  // reserved for the mount in Step 7.
  KeyC:'duel',
};

const GAMEPAD_MAP = [
  [0,'dodge'], [2,'light'], [3,'heavy'], [1,'musou'],
  [5,'sprint'], [7,'sprint'], [4,'lockon'], [9,'pause'],
];
const deadzone = v => (Math.abs(v) < 0.22 ? 0 : (v - Math.sign(v) * 0.22) / 0.78);

/** Buffered, edge-detected input for keyboard + mouse + gamepad. */
export function createInput(canvas) {
  const state = {
    move: { x: 0, y: 0 }, moveLen: 0,
    look: { x: 0, y: 0, z: 0 }, _lookRaw: { x: 0, y: 0, z: 0 }, sensitivity: 1,
    down: new Set(), pressedSet: new Set(), releasedSet: new Set(),
    buffer: new Map(), pointerLocked: false, gamepad: false, anyInput: false,
    pressed(n) { return this.pressedSet.has(n); },
    held(n) { return this.down.has(n); },
    released(n) { return this.releasedSet.has(n); },
    /** Consume a buffered press within `window` seconds. */
    consume(n, window = 0.24) {
      const t = this.buffer.get(n);
      if (t !== undefined && t <= window) { this.buffer.delete(n); return true; }
      return false;
    },
    peek(n, window = 0.24) { const t = this.buffer.get(n); return t !== undefined && t <= window; },
    clearBuffer() { this.buffer.clear(); },
    setSensitivity(v) { this.sensitivity = clamp(Number.isFinite(v) ? v : 1, 0.25, 2.5); return this.sensitivity; },
    /** Test hook */
    _virtual: null,
  };
  const press = (n) => { if (!state.down.has(n)) { state.down.add(n); state.pressedSet.add(n); state.buffer.set(n, 0); state.anyInput = true; } };
  const release = (n) => { if (state.down.delete(n)) state.releasedSet.add(n); };

  const onKey = (e, isDown) => {
    const n = KEYMAP[e.code]; if (!n) return;
    if (e.code === 'Tab' || e.code === 'Space') e.preventDefault();
    isDown ? press(n) : release(n);
  };
  const kd = e => { if (!e.repeat) onKey(e, true); };
  const ku = e => onKey(e, false);
  const md = e => { if (e.button === 0) press('light'); else if (e.button === 2) press('heavy'); else if (e.button === 1) press('musou'); };
  const mu = e => { if (e.button === 0) release('light'); else if (e.button === 2) release('heavy'); else if (e.button === 1) release('musou'); };
  const mm = e => { if (document.pointerLockElement === canvas) { state._lookRaw.x += e.movementX; state._lookRaw.y += e.movementY; } };
  const ctx = e => e.preventDefault();
  const wheel = e => { state._lookRaw.z += Math.sign(e.deltaY); };

  // Sample device state before gameplay's fixed step. Look remains a render-
  // frame intent so a catch-up frame cannot multiply mouse/stick rotation.
  const sample = (includeLook) => {
    const pads = navigator.getGamepads?.() || [];
    let pad = null; for (const p of pads) if (p && p.connected) { pad = p; break; }
    state.gamepad = !!pad;
    let mx = 0, my = 0;
    if (state.down.has('left')) mx -= 1; if (state.down.has('right')) mx += 1;
    if (state.down.has('up')) my -= 1; if (state.down.has('down')) my += 1;
    if (pad) {
      mx += deadzone(pad.axes[0] || 0); my += deadzone(pad.axes[1] || 0);
      if (includeLook) {
        state._lookRaw.x += deadzone(pad.axes[2] || 0) * 14;
        state._lookRaw.y += deadzone(pad.axes[3] || 0) * 14;
      }
      const b = pad.buttons;
      for (let i = 0; i < GAMEPAD_MAP.length; i++) {
        const m = GAMEPAD_MAP[i]; if (b[m[0]]?.pressed) press(m[1]); else release(m[1]);
      }
    }
    if (state._virtual) {
      const v = state._virtual;
      mx = v.move?.x ?? mx; my = v.move?.y ?? my;
      if (includeLook) { state._lookRaw.x += v.look?.x || 0; state._lookRaw.y += v.look?.y || 0; }
      if (v.press) for (const n of v.press) press(n);
      if (v.release) for (const n of v.release) release(n);
    }
    const len = Math.hypot(mx, my);
    if (len > 1) { mx /= len; my /= len; }
    state.move.x = mx; state.move.y = my; state.moveLen = clamp(len, 0, 1);
  };

  return {
    name: 'input', order: ORDER.INPUT,
    init(w) {
      w.input = state;
      addEventListener('keydown', kd); addEventListener('keyup', ku);
      addEventListener('blur', () => { state.down.clear(); });
      canvas.addEventListener('mousedown', md); addEventListener('mouseup', mu);
      addEventListener('mousemove', mm); canvas.addEventListener('contextmenu', ctx);
      canvas.addEventListener('wheel', wheel, { passive: true });
      canvas.addEventListener('click', () => { if (!w.cfg.debug.headless) canvas.requestPointerLock?.(); });
      document.addEventListener('pointerlockchange', () => { state.pointerLocked = document.pointerLockElement === canvas; });
    },
    fixedUpdate() { sample(false); },
    update(w, dt, alpha, realDt) {
      // Buffer ages in real time so hitstop never eats inputs.
      for (const [k, t] of state.buffer) { const n = t + realDt; if (n > 1.2) state.buffer.delete(k); else state.buffer.set(k, n); }
      sample(true);
      state.look.x = state._lookRaw.x * state.sensitivity;
      state.look.y = state._lookRaw.y * state.sensitivity;
      state.look.z = state._lookRaw.z;
    },
    lateUpdate() {
      state.pressedSet.clear(); state.releasedSet.clear();
      state.look.x = 0; state.look.y = 0; state.look.z = 0;
      state._lookRaw.x = 0; state._lookRaw.y = 0; state._lookRaw.z = 0;
    },
    dispose() { removeEventListener('keydown', kd); removeEventListener('keyup', ku); removeEventListener('mousemove', mm); }
  };
}
